import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tooltip from '@mui/material/Tooltip';
import LeftAngle from '../../assets/icons/left-angle.svg';
import RightAngle from '../../assets/icons/right-angle.svg';
import LoadingSpinner from '../loadingSpinner/loadingSpinner';
import InfoIcon from '../../assets/icons/info-icon.svg';
import EmptyBigCard from '../dashboard/dashboardCards/emptyBigCard';
import { TOP_PRODUCTS_BY_AD } from '../../constants/pageIdentifiers';
import { useDashboardFilterDateStore } from '../../store/dateRangeStore';
import AdsReportTable from '../../partials/adsReportTable';
import { TableColumnInterface } from '../../interfaces/tableColumn.interface';
import VatDisplayer from '../../partials/VATDisplayer';
import { useGetTopProductsByAd } from '../../apiHooks/queries/reports/useGetTopProductsByAd';

const fixedColumns: TableColumnInterface[] = [
  {
    field: 'adCode',
    headerName: 'adCode',
    cellRenderer: (params: string) => (
      <Tooltip arrow placement="top" title={params}>
        <span className="max-w-[130px] truncate font-medium 2xl:max-w-[160px]">{params || '-'}</span>
      </Tooltip>
    ),
  },
  {
    field: 'itemName',
    headerName: 'productName',
    cellRenderer: (params: string) => (
      <Tooltip arrow placement="top" title={params}>
        <span className="max-w-[130px] truncate 2xl:max-w-[160px]">{params || '-'}</span>
      </Tooltip>
    ),
  },
];

const columns: TableColumnInterface[] = [
  {
    field: 'quantity',
    headerName: 'quantity',
    cellRenderer: (params) => (
      <span>{params ? Math.round(params).toLocaleString() : 0}</span>
    ),
  },
  {
    field: 'revenue',
    headerName: 'revenue',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
  {
    field: 'profit',
    headerName: 'profit',
    cellRenderer: (params) => <VatDisplayer value={params} />,
  },
];

const TopProductsByAdReport: FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const dashboardFilterDateRange = useDashboardFilterDateStore((state) => state.dashboardFilterDateRange);

  const { data, isFetching, isError } = useGetTopProductsByAd(
    TOP_PRODUCTS_BY_AD,
    dashboardFilterDateRange[0],
    dashboardFilterDateRange[1],
    page,
  );

  const reportData = data?.data?.data?.report;
  const hasNextPage = data?.data?.data?.hasNextPage;

  return (
    <div className="flex h-full flex-col text-clip rounded-lg">
      <div className="flex max-h-[47px] items-center justify-between border-b border-dark px-3 py-2">
        <div className="flex items-center py-2 gap-4">
          <p className="text-lg font-medium leading-[36px] text-primary xl:text-base">
            {t('topProductsByAd')}
          </p>
          <Tooltip title={t('topProductsByAdInfo')}>
            <img src={InfoIcon} alt="" className="max-w-[20px]" />
          </Tooltip>
        </div>
      </div>
      <div className="flex h-[465px] w-full flex-col">
        {!isFetching && !isError && reportData === null && <EmptyBigCard />}
        {isFetching && <LoadingSpinner center color="primary" />}
        {isError && !isFetching && <p>{t('errorRetrievingData')}</p>}
        {!isFetching && !isError && reportData !== null && (
          <AdsReportTable
            data={reportData}
            isFetching={isFetching}
            isError={isError}
            columns={columns}
            fixedColumns={fixedColumns}
          />
        )}
      </div>
      <div className="mb-[22px] mt-3 flex items-center justify-center gap-4">
        <button
          onClick={() => setPage(page - 1)}
          disabled={page === 0 || isFetching}
          className="cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10 "
        >
          <img src={LeftAngle} alt="Previous" />
        </button>
        <button
          onClick={() => setPage(page + 1)}
          disabled={!hasNextPage || isFetching}
          className="cursor-pointer hover:bg-primary/20 active:bg-primary/30'}
               grid h-[28px] w-[28px] place-content-center rounded-lg bg-primary/10 "
        >
          <img src={RightAngle} alt="Next" />
        </button>
      </div>
    </div>
  );
};

export default TopProductsByAdReport;
